import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { AxiosError, AxiosResponse } from 'axios';
import ErrorHandler from '@/support/ErrorHandler';
import MiFileUpload from '@/components/mi-file-upload/MiFileUpload';
import { addClass, removeClass } from '@/support/Html';
import LoaderCard from '@/components/loader-card/LoaderCard';
import { Brand } from '@/models/Brand';
import { Supplier } from '@/models/Supplier';
import debounce from 'debounce';
import { Product } from '@/models/Product';
import { axiosDownload } from '@/support/Client';

@Component<UploadProductCsvDialog>({
  components: {
    MiFileUpload,
    LoaderCard
  },
})
export default class UploadProductCsvDialog extends Vue {
  @Prop()
  protected product!: Product;

  protected selected: any[] = [];
  protected isLoading: boolean = false;
  protected activeStepper: boolean = true;
  protected currentStep: number = 1;

  protected isUploadingCsv: boolean = false;
  protected selectedCsvFile: File | null = null;
  protected uploadResponse: object | null = null;

  protected brands: Brand[] = [];
  protected suppliers: Supplier[] = [];
  protected selectedBrand: string = '';
  protected selectedSupplier: string = '';

  protected acceptedFileTypes = [
    'text/csv',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
  ];
  protected uploadedFile: string = '';

  public searchBrandDebounce: Function = this.handleBrandSearch();
  public searchSupplierDebounce: Function = this.handleSupplierSearch();
  protected brandSearch: string = '';
  protected supplierSearch: string = '';

  protected handleBrandSearch() {
    return debounce((searchInput: string) => {
      this.getBrands(searchInput);
    }, 400);
  }

  protected handleSupplierSearch() {
    return debounce((searchInput: string) => {
      this.getSuppliers(searchInput);
    }, 400);
  }

  mounted() {
    const pageHtml = document.querySelectorAll('html')[0];
    addClass(pageHtml, 'overflow-y-hidden');
    if (this.product) {
      this.selectedBrand = this.product.brand && this.product.brand.id || '';
      this.selectedSupplier = this.product.supplier && this.product.supplier.id || '';
    }
    this.getBrands();
    this.getSuppliers();
  }


  protected getBrands(filter: string = '') {
    new Brand()
    .filter('search', filter)
    .limit(100)
    .all()
    .then((brands: Brand[]) => {
      this.brands = brands;
    })
    .catch((error: AxiosError) => {
    });
  }

  protected getSuppliers(filter: string = '') {
    new Supplier()
    .filter('search', filter)
    .limit(100)
    .all()
    .then((suppliers: Supplier[]) => {
      this.suppliers = suppliers;
    })
    .catch((error: AxiosError) => {
    });
  }

  protected goToStep(step: number) {
    if (this.currentStep < step || !this.activeStepper) {
      return;
    }

    this.currentStep = step;
  }

  protected get hasBrandAndSupplier() {
    return this.selectedBrand.length && this.selectedSupplier.length;
  }

  protected close() {
    this.$emit('input', false);
    this.currentStep = 1;
    this.clear();
    const pageHtml = document.querySelectorAll('html')[0];
    removeClass(pageHtml, 'overflow-y-hidden');
  }

  protected clear() {
    this.selectedBrand = '';
    this.selectedSupplier = '';
    this.selectedCsvFile = null;
  }

  protected handleItemDropped(file: File) {
    if(!file) {
      return;
    }

    this.selectedCsvFile = file;
    const fileUploadComponent = this.$refs.fileUpload as MiFileUpload;
    fileUploadComponent.uploaded();
  }

  protected create() {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.currentStep = 4;

    const payload = {
      brand: this.selectedBrand,
      supplier: this.selectedSupplier,
      products: this.selectedCsvFile,
    };

    new Product()
      .importProducts()
      .create(payload)
      .then((response: AxiosResponse) => {
        this.isLoading = false;
        if (response) {
          this.uploadResponse = response;
        }
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
        this.isLoading = false;
        this.currentStep = 3;
      })
  }

  protected async downloadTemplateCsv(): Promise<void> {
    try {
      await axiosDownload(
        {
          url: new Product().exportProducts(),
          accept: 'text/csv; charset=utf-8'
        },
        {}
      );
    } catch (error) {
      ErrorHandler.network(error);
    }
  }

  protected get chosenBrand() {
    const brand = this.brands.filter((brand: Brand) => {
      return brand.id === this.selectedBrand;
    });

    return brand;
  }

  protected get chosenSupplier() {
    const supplier = this.suppliers.filter((supplier: Supplier) => {
      return supplier.id === this.selectedSupplier;
    });

    return supplier;
  }

  @Watch('brandSearch')
  protected brandSearchChanged(brand: string) {
    this.searchBrandDebounce(brand);
  }

  @Watch('supplierSearch')
  protected supplierSearchChanged(brand: string) {
    this.searchSupplierDebounce(brand)
  }
}
